import { Box, Stack, Theme, Typography, useMediaQuery } from "@mui/material";
import { useFormat } from "@sinch/core/hooks/format";
import { isNotNil } from "ramda-adjunct";
import React, { FC, ReactNode } from "react";
import { DateRange } from "@sinch/components";
import { JobPill, OrderPill } from "@sinch/components/Pill/Pill";

export type JobNameProps = {
  id?: number | string | null;
  orderNumber?: number | string | null;
  name?: ReactNode | null;
  variant?: "smallTitle" | "regularLine";
} & (
  | {
      capacity: number;
      staffCount: number;
    }
  | {
      capacity?: undefined;
      staffCount?: undefined;
    }
) &
  (
    | {
        beginning: Date;
        end?: undefined;
      }
    | {
        beginning?: undefined;
        end?: undefined;
      }
  );

export const JobName: FC<JobNameProps> = ({ orderNumber, variant, name, capacity, staffCount, id, beginning, end }) => {
  const isMobile = useMediaQuery((theme: Theme) => theme.breakpoints.down("sm"));
  const { date } = useFormat();

  return (
    <Stack alignItems="center" direction="row" flexWrap={isMobile ? "wrap" : "nowrap"} spacing={1}>
      {name && (
        <Typography
          component="span"
          sx={(theme) => ({
            fontSize: variant === "regularLine" ? theme.typography.body1.fontSize : theme.typography.pxToRem(15),
          })}
        >
          {name}
        </Typography>
      )}
      {isNotNil(staffCount) && isNotNil(capacity) && (
        <Typography
          component="span"
          sx={(theme) => ({
            fontSize: variant === "regularLine" ? theme.typography.body1.fontSize : theme.typography.pxToRem(15),
            color: staffCount < capacity ? theme.palette.error.main : theme.palette.success.main,
          })}
        >
          ({staffCount}/{capacity})
        </Typography>
      )}
      {orderNumber && (
        <Box>
          <OrderPill id={orderNumber} size="small" />
        </Box>
      )}
      {id && (
        <Box>
          <JobPill id={id} size="small" />
        </Box>
      )}
      {beginning && !end && (
        <Typography
          component="span"
          sx={({ typography }) => ({
            whiteSpace: "nowrap",
            fontSize: variant === "regularLine" ? typography.descriptionTiny.fontSize : typography.body1.fontSize,
          })}
        >
          {date.full(beginning)}
        </Typography>
      )}
      {beginning && end && (
        <Typography
          component="span"
          sx={({ typography }) => ({
            whiteSpace: "nowrap",
            fontSize: variant === "regularLine" ? typography.descriptionTiny.fontSize : typography.body1.fontSize,
          })}
        >
          <DateRange end={end} start={beginning} />
        </Typography>
      )}
    </Stack>
  );
};
